import { useEffect, useRef } from "react";
import { Check } from "lucide-react";

import { cn } from "src/lib/utils/utils";
import { Eyebrow } from "./ui";

/**
 * Every question at a glance, and a way to jump to any of them.
 *
 * A column beside the question on wide screens; on a phone there is no room
 * for a column, so the same buttons run as a strip above the question and
 * scroll sideways when there are many.
 *
 * The circle is the answered marker. The number carries the position and the
 * circle carries the state, so the two never compete for the same mark.
 *
 * The strip's scrollbar is hidden: the chip cut off at the edge already says
 * there is more, and the current question is kept scrolled into view.
 */
const QuestionNav = ({
  total,
  current,
  isAnswered,
  answeredCount,
  onSelect,
  disabled,
}: {
  total: number;
  current: number;
  isAnswered: (index: number) => boolean;
  answeredCount: number;
  onSelect: (index: number) => void;
  disabled?: boolean;
}) => {
  const currentRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [current]);

  return (
    <nav
      aria-label="Questions"
      className="lg:sticky lg:top-10 lg:rounded-2xl lg:border lg:border-border lg:bg-card lg:p-4 lg:shadow-ui-sm"
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <Eyebrow>Questions</Eyebrow>
        <span className="text-xs tabular-nums text-muted-foreground">
          {answeredCount}/{total} answered
        </span>
      </div>

      <ol className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:max-h-[calc(100vh-12rem)] lg:flex-col lg:gap-1 lg:overflow-y-auto lg:overflow-x-hidden lg:px-0 lg:pb-0">
        {Array.from({ length: total }, (_, index) => {
          const isCurrent = index === current;
          const answered = isAnswered(index);

          return (
            <li key={index} className="shrink-0">
              <button
                ref={isCurrent ? currentRef : undefined}
                type="button"
                onClick={() => onSelect(index)}
                disabled={disabled}
                aria-current={isCurrent ? "step" : undefined}
                aria-label={`Question ${index + 1}, ${
                  answered ? "answered" : "not answered"
                }`}
                className={cn(
                  "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold tabular-nums transition-colors disabled:cursor-not-allowed disabled:opacity-60 lg:w-full lg:justify-between",
                  isCurrent
                    ? "border-brand-navy bg-brand-navy text-white"
                    : "border-border bg-card text-brand-navy hover:border-brand-navy/40 lg:border-transparent lg:bg-transparent lg:hover:border-transparent lg:hover:bg-surface",
                )}
              >
                <span>
                  <span className="hidden lg:inline">Question </span>
                  {index + 1}
                </span>

                <span
                  aria-hidden="true"
                  className={cn(
                    "grid h-4 w-4 shrink-0 place-items-center rounded-full border transition-colors",
                    answered
                      ? "border-brand-orange bg-brand-orange text-white"
                      : isCurrent
                        ? "border-white/60"
                        : "border-brand-navy/30",
                  )}
                >
                  {answered && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default QuestionNav;
